/**
 * WorkflowBar — renders the incident lifecycle as a horizontal stepper with
 * the current status highlighted and legal next-transition buttons exposed.
 *
 * Each transition button is enabled only if the backend reports its
 * prerequisites satisfied. Blocked buttons carry a tooltip explaining what's
 * missing, so the user understands what to do next instead of silently
 * failing on submit.
 *
 * The approve action is a special case — the backend requires compliance-lead
 * role AND separation of duties, so this component surfaces a clear message
 * if the current user is the reporter.
 */

import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const STATUS_LABELS = {
  draft: 'Draft',
  under_investigation: 'Under investigation',
  risk_assessment_complete: 'Risk assessment complete',
  determination_pending_approval: 'Pending approval',
  approved_not_breach: 'Not a breach',
  approved_breach: 'Breach',
  notification_complete: 'Notification complete',
  closed: 'Closed'
};

// The stepper has two variants:
//   • Breach path     — ends in notification_complete → closed
//   • Non-breach path — branches at determination_pending_approval → closed
// The component picks which one to render based on the current status. Closed
// incidents default to the breach view (we can't tell the history at render
// time without an extra query).
const BREACH_STEPS = [
  'draft',
  'under_investigation',
  'risk_assessment_complete',
  'determination_pending_approval',
  'approved_breach',
  'notification_complete',
  'closed'
];

const NONBREACH_STEPS = [
  'draft',
  'under_investigation',
  'risk_assessment_complete',
  'determination_pending_approval',
  'approved_not_breach',
  'closed'
];

function stepperFor(currentStatus) {
  if (currentStatus === 'approved_not_breach') return NONBREACH_STEPS;
  return BREACH_STEPS;
}

export default function WorkflowBar({ incident, incidentId, onRefresh }) {
  const [options, setOptions] = useState([]);
  const [sod, setSod] = useState({ ok: true, reason: null, current_user_is_reporter: false });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null); // target status currently submitting
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/privacy/incidents/${incidentId}/workflow-options`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setOptions(data.options || []);
        setSod(data.separation_of_duties || { ok: true });
      } else {
        setError(data.message || 'Failed to load workflow options');
      }
    } catch (e) {
      setError(e.message || 'Failed to load workflow options');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [incidentId, incident?.status]);

  const handleTransition = async (target) => {
    // Approval transitions go through a different endpoint — this component
    // doesn't initiate them. The Approval tab handles that.
    if (target === 'approved_breach' || target === 'approved_not_breach') return;
    setSubmitting(target);
    setError('');
    try {
      const res = await fetch(`${API}/api/privacy/incidents/${incidentId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: target })
      });
      const data = await res.json();
      if (data.success) {
        onRefresh?.();
      } else {
        const missing = data.missing_prerequisites || [];
        setError(
          missing.length > 0
            ? `${data.message}: ${missing.join(' · ')}`
            : (data.message || 'Transition failed')
        );
      }
    } catch (e) {
      setError(e.message || 'Transition failed');
    } finally {
      setSubmitting(null);
    }
  };

  const currentStatus = incident?.status;
  const stepperStates = stepperFor(currentStatus);
  const currentIndex = stepperStates.indexOf(currentStatus);
  const isTerminal = currentStatus === 'closed';

  return (
    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Stepper strip */}
      <div className="px-6 pt-5 pb-4 bg-gradient-to-br from-slate-50 to-white">
        <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">Incident lifecycle</p>
        <div className="flex items-center gap-1 overflow-x-auto pb-1">
          {stepperStates.map((s, i) => {
            const isActive = s === currentStatus;
            const isPassed = currentIndex >= 0 && i < currentIndex;
            // "approved_not_breach" gets a green accent instead of the blue one
            // used for the active step — it's a terminal positive outcome.
            const isNonBreachActive = isActive && s === 'approved_not_breach';
            return (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-2 flex-shrink-0 px-3 py-1.5 rounded-xl transition-all ${
                  isNonBreachActive ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-md shadow-emerald-600/25' :
                  isActive ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-600/25' :
                  isPassed ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' :
                  'text-slate-400'
                }`}>
                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                    isNonBreachActive ? 'bg-white text-emerald-700' :
                    isActive ? 'bg-white text-blue-700' :
                    isPassed ? 'bg-emerald-500 text-white' :
                    'bg-slate-200 text-slate-500'
                  }`}>
                    {isPassed ? '✓' : i + 1}
                  </span>
                  <span className="text-xs font-semibold whitespace-nowrap">{STATUS_LABELS[s]}</span>
                </div>
                {i < stepperStates.length - 1 && (
                  <div className={`w-4 h-px flex-shrink-0 ${isPassed ? 'bg-emerald-300' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Transition action row */}
      <div className="px-6 py-4 border-t border-slate-100 bg-white flex flex-wrap items-center gap-3">
        {loading ? (
          <p className="text-xs text-slate-400">Loading allowed transitions...</p>
        ) : isTerminal ? (
          <p className="text-xs text-slate-500 italic">
            Incident closed — no further transitions. All records are immutable.
          </p>
        ) : options.length === 0 ? (
          <p className="text-xs text-slate-400">No transitions available from this state.</p>
        ) : (
          <>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mr-1">Move to</span>
            {options.map((opt) => {
              const isApproval = opt.target === 'approved_breach' || opt.target === 'approved_not_breach';
              const disabled = !opt.ok || submitting != null || isApproval;
              const tooltipText = isApproval
                ? 'Approval transitions must be performed from the Approval tab by a compliance lead who was not the incident reporter.'
                : !opt.ok
                  ? `Missing: ${opt.missing.join(' · ')}`
                  : '';
              return (
                <div key={opt.target} className="relative group">
                  <button
                    type="button"
                    onClick={() => handleTransition(opt.target)}
                    disabled={disabled}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                      disabled
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-blue-600/30'
                    }`}
                  >
                    {!opt.ok && (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    )}
                    {submitting === opt.target ? 'Moving...' : opt.label}
                    {isApproval && (
                      <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600">via Approval tab</span>
                    )}
                  </button>
                  {tooltipText && (
                    <div className="absolute left-0 top-full mt-2 w-72 px-3 py-2 bg-slate-900 text-white text-xs rounded-xl shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none z-20 transition-opacity">
                      {tooltipText}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Separation-of-duties warning (persistent when applicable) */}
      {sod.current_user_is_reporter && currentStatus === 'determination_pending_approval' && (
        <div className="mx-6 mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3">
          <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-amber-900">Separation of duties required</p>
            <p className="text-xs text-amber-800 mt-0.5">
              You reported this incident, so you cannot also approve the determination. Another compliance lead must review and approve.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="mx-6 mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}
    </div>
  );
}
